import { SystemStyleObject, defineStyle } from '@chakra-ui/react';
import {
  getRadiusToken,
  getTypographyToken,
  ColorMode,
  getBoxSizingStyles,
} from '@allergan-data-labs/alle-elements-core';
import { ButtonSizes } from './button';
import { TextButtonColorScheme } from './text-button';

interface VariantAttributes {
  colorMode: ColorMode;
  hoverFocusTextColor: string;
  activeTextColor: string;
  textColor: string;
  disabledTextColor: string;
}

const baseStyle = () => {
  return defineStyle({
    ...getTypographyToken('Body/Medium/Regular'),
    borderRadius: getRadiusToken('Medium small'),
    whiteSpace: 'nowrap',
  });
};

const createVariantStyles = ({
  colorMode,
  textColor,
  hoverFocusTextColor,
  activeTextColor,
  disabledTextColor,
}: VariantAttributes) => {
  const outlineStyles = {
    bg: 'transparent',
    color: hoverFocusTextColor,
    outlineStyle: 'solid',
    outlineWidth: '4px',
    outlineColor: `${colorMode}.Border/Brand/Focus ring/Default`,
    borderRadius: getRadiusToken('Medium small'),
  };

  const styles: SystemStyleObject = {
    bg: 'transparent',
    color: textColor,
    _hover: {
      bg: 'transparent',
      color: hoverFocusTextColor,
      _disabled: {
        color: disabledTextColor,
        boxShadow: 'none',
      },
    },
    _focusVisible: { ...outlineStyles },
    _active: {
      bg: 'transparent',
      outline: 'none',
      color: activeTextColor,
    },
    _disabled: {
      cursor: 'not-allowed',
      opacity: 1,
      bg: 'transparent',
      color: disabledTextColor,
      boxShadow: 'none',
      border: 'none',
    },
  };

  return defineStyle(styles);
};

const sizes = {
  xs: defineStyle({
    ...getTypographyToken('Body/Extra small/Regular'),
    px: '.375rem',
    py: '0',
    maxHeight: '18px',
    height: '18px',
  }),
  sm: defineStyle({
    ...getTypographyToken('Body/Small/Regular'),
    px: '.5rem',
    py: '0',
    maxHeight: '24px',
    height: '24px',
  }),
  md: defineStyle({
    ...getTypographyToken('Body/Medium/Regular'),
    px: '.5rem',
    py: '0',
    maxHeight: '24px',
    height: '24px',
  }),
  lg: defineStyle({
    ...getTypographyToken('Body/Large/Medium'),
    px: '1rem',
    py: '0',
    // Defining a strict height for the large button causes a misalignment between the text and icons
  }),
};

export const generateTextButtonTheme = (
  colorScheme: TextButtonColorScheme,
  colorMode: ColorMode,
  size: ButtonSizes
): SystemStyleObject => {
  const standardStyles = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    userSelect: 'none',
  };

  const variants = {
    action: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Icon/Neutral/Interactive/Enable`,
      hoverFocusTextColor: `${colorMode}.Icon/Neutral/Interactive/Hover`,
      activeTextColor: `${colorMode}.Icon/Neutral/Interactive/Pressed`,
      disabledTextColor: `${colorMode}.Icon/Neutral/Disabled/Default`,
    }),
    brand: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Icon/Brand/Interactive/Enable`,
      hoverFocusTextColor: `${colorMode}.Icon/Brand/Interactive/Hover`,
      activeTextColor: `${colorMode}.Icon/Brand/Interactive/Pressed`,
      disabledTextColor: `${colorMode}.Icon/Neutral/Disabled/Default`,
    }),
    negative: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Icon/Status/Interactive/Enable negative`,
      hoverFocusTextColor: `${colorMode}.Icon/Status/Interactive/Hover negative`,
      activeTextColor: `${colorMode}.Icon/Status/Interactive/Pressed negative`,
      disabledTextColor: `${colorMode}.Icon/Neutral/Disabled/Default`,
    }),
    positive: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Icon/Status/Interactive/Enable positive`,
      hoverFocusTextColor: `${colorMode}.Icon/Status/Interactive/Enable positive`,
      activeTextColor: `${colorMode}.Icon/Status/Interactive/Enable positive`,
      disabledTextColor: `${colorMode}.Icon/Neutral/Disabled/Default`,
    }),
  };

  const styles: SystemStyleObject = {
    ...getBoxSizingStyles(),
    cursor: 'pointer',
    ...baseStyle,
    ...variants[colorScheme],
    ...sizes[size],
    ...standardStyles,
  };

  return styles;
};

export const getIconStyles = (
  side: 'left' | 'right',
  size: ButtonSizes
): SystemStyleObject => {
  const styles: SystemStyleObject = {
    color: 'inherit',
  };

  if (side === 'left') {
    if (size === 'md' || size === 'sm') {
      styles.paddingRight = '.5rem';
    }

    if (size === 'lg') {
      styles.paddingRight = '1rem';
    }

    if (size === 'xs') {
      styles.paddingRight = '.375rem';
    }
  }

  if (side === 'right') {
    if (size === 'md' || size === 'sm') {
      styles.paddingLeft = '.5rem';
    }

    if (size === 'lg') {
      styles.paddingLeft = '1rem';
    }

    if (size === 'xs') {
      styles.paddingLeft = '.375rem';
    }
  }

  return styles;
};
